import {
    DataGrid,
    GridColDef,
    GridRowId,
    GridRowsProp,
    GridToolbarExport,
    GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { SoecoButton } from "../../components/SoecoButton";
import { Inventory, MergedInventory } from "../../types";
import { ArrowLeft, ArrowRight, Edit3, Rows } from "lucide-react";
import { useState } from "react";
import { Pagination, PaginationItem, Stack, Box } from "@mui/material";

type Props = {
    inventories: Inventory[];
    companyId: string;
    mergedInventories?: MergedInventory[];
};

const CustomToolbar = ({
    companyId,
    mergedInventories,
    showMerged,
    onToggleMerged,
}: {
    companyId: string;
    mergedInventories: MergedInventory[];
    showMerged: boolean;
    onToggleMerged: () => void;
}) => {
    const navigate = useNavigate();

    const handleMergeClick = () => {
        navigate(`/companies/${companyId}/merge-inventories`);
    };

    return (
        <Box sx={sx}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <GridToolbarExport />
                {/* <SoecoButton
                    size="medium"
                    color={"inherit"}
                    onClick={onToggleMerged}
                    disabled={!mergedInventories || mergedInventories.length < 1}
                >
                    {showMerged ? "Inventories" : "Shared inventories"}
                </SoecoButton> */}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", marginLeft: 2 }}>
                <GridToolbarQuickFilter
                    debounceMs={500}
                    quickFilterParser={value => value.split(",").map(v => v.trim())}
                    placeholder="Search"
                    variant="outlined"
                />
            </Box>
        </Box>
    );
};

export const InventoryList = ({ inventories, companyId, mergedInventories }: Props) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize] = useState(100);
    const [showMerged, setShowMerged] = useState(false);

    const handleOnRowClick = (id: GridRowId) => {
        if (showMerged) {
            navigate(`/companies/${companyId}/inventories/${id}`, {
                state: { isMerged: true },
            });
        } else {
            navigate(`/companies/${companyId}/inventories/${id}`, {
                state: { isMerged: false },
            });
        }
    };

    const handleInventoryEdit = (id: GridRowId) => {
        navigate(`/companies/${companyId}/editInventories/${id}`);
    };

    const baseColumns: GridColDef[] = [
        { field: "name", headerName: "Name", flex: 0.5 },
        { field: "project", headerName: "Project", flex: 0.2 },
        { field: "date", headerName: "Date", flex: 0.3, type: "date" },
        {
            field: "actions",
            headerName: "Action",
            align: "left",
            flex: 0.2,
            type: "actions",
            renderCell: params => (
                <TableActions>
                    <SoecoButton
                        icon={true}
                        size="small"
                        onClick={() => handleInventoryEdit(params.id)}
                    >
                        <Edit3 size={24} />
                    </SoecoButton>
                </TableActions>
            ),
        },
    ];

    const columns = showMerged ? baseColumns.filter(col => col.field !== "actions") : baseColumns;

    const activeList = showMerged ? mergedInventories ?? [] : inventories;

    const rows: GridRowsProp = [
        ...activeList.map(inventory => ({
            id: inventory.id,
            name: inventory.name,
            project: inventory.project,
            date: inventory.estimatedDeliveryDate?.slice(0, 10),
        })),
        // Lägg bara till "Merged inventory" om showMerged är false
        ...(showMerged
            ? []
            : [
                  {
                      id: "f4c7c6b0-3c4b-4c3b-9c9b-0b0b0b0b0b0b",
                      name: "Merged inventory",
                      project: "",
                      date: new Date().toISOString().slice(0, 10),
                      sx: {
                          "&:hover": {
                              cursor: "pointer",
                          },
                          color: "red",
                      },
                  },
              ]),
    ];

    const numberOfPages = Math.ceil(rows.length / pageSize);
    const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);

    return (
        <InventoryListWrapper>
            <DataGrid
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                page={page - 1}
                rowsPerPageOptions={[100]}
                hideFooter
                onRowClick={params => handleOnRowClick(params.id)}
                initialState={{
                    sorting: { sortModel: [{ field: "date", sort: "desc" }] },
                }}
                components={{ Toolbar: CustomToolbar }}
                componentsProps={{
                    toolbar: {
                        companyId,
                        mergedInventories: mergedInventories ?? [],
                        showMerged,
                        onToggleMerged: () => setShowMerged(prev => !prev),
                    },
                }}
            />
            <Stack
                spacing={2}
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                marginTop={2}
            >
                <Pagination
                    shape="rounded"
                    color="primary"
                    page={page}
                    onChange={handlePageChange}
                    count={numberOfPages}
                    renderItem={item => (
                        <PaginationItem
                            {...item}
                            components={{
                                previous: () => (
                                    <ArrowLeft style={{ cursor: "pointer" }} size={16} />
                                ),
                                next: () => <ArrowRight style={{ cursor: "pointer" }} size={16} />,
                            }}
                            sx={{
                                backgroundColor: item.selected ? "black" : "transparent",
                                color: item.selected ? "white" : "black",
                                "&:hover": {
                                    backgroundColor: item.selected ? "black" : "lightgrey",
                                },
                                fontFamily: "'Yllw Sans a1 241219', sans-serif",
                            }}
                        />
                    )}
                />
            </Stack>
        </InventoryListWrapper>
    );
};

const InventoryListWrapper = styled.div`
    width: 100%;
`;

const TableActions = styled.div`
    display: flex;
    font-size: 6px;
`;

const sx = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: "8px 16px",
    flexWrap: "nowrap",
    "& .MuiButton-root": {
        backgroundColor: "white",
        color: "#8c8c8c",
        border: "1px solid #9e9e9e",
        marginLeft: "12px",
        fontFamily: "'Inter', sans-serif",
        fontWeight: "600",
        height: "42px",
        minHeight: "42px",
        padding: "0 12px",
        fontSize: "14px",
        textTransform: "none",
    },
    "& .MuiTextField-root": {
        borderRadius: "20px",
        "& .MuiInputBase-root": {
            borderRadius: "50px",
            color: "black",
            border: "1px solid #9e9e9e",
            fontFamily: "'Yllw Sans a1 241219', sans-serif",
            fontWeight: "400",
            height: "42px",
            minHeight: "42px",
            padding: "0 8px",
            fontSize: "14px",
        },
    },
};
